import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DemoInstructorPanel from "./DemoInstructorPanel";
import DemoSpotlightOverlay from "./DemoSpotlightOverlay";
import HighlightedCreativeText from "../HighlightedCreativeText";
import { getPerformanceBand } from "../lessonPerformanceBand";
import { loadDemoLesson } from "../../utils/demoLessonLoader";
import { postJSON } from "../../services/api";
import "./DemoLessonFlow.css";

const STEP_IDS = [
  "welcome",
  "read_task",
  "start_writing",
  "reach_minimum",
  "notice_word_count",
  "click_feedback",
  "review_feedback",
  "coachFeedback",
  "inspect_writing_signals",
  "complete",
];
const START_WRITING_STEP_INDEX = STEP_IDS.indexOf("start_writing");
const MAX_RESPONSE_CHARS = 12000;
const DEMO_ANNOTATION_MAX_CHARS = 2000;

function countWords(input) {
  const s = String(input || "").trim();
  if (!s) return 0;
  return s.split(/\s+/).filter(Boolean).length;
}

function canAdvanceStep(stepId, state) {
  if (stepId === "read_task") return state.readTaskConfirmed;
  if (stepId === "review_feedback") return state.feedbackVisible;
  if (stepId === "coachFeedback") return state.coachFeedbackVisible;
  if (stepId === "inspect_writing_signals")
    return state.showWritingSignals || state.allowInspectStepNext;
  return true;
}

function getStepTarget(stepId) {
  const byStep = {
    read_task: "demo-task",
    start_writing: "demo-textarea",
    reach_minimum: "demo-writing-focus",
    notice_word_count: "demo-wordcount",
    click_feedback: "demo-feedback-button",
    review_feedback: "demo-feedback-panel",
    coachFeedback: "demo-coach-feedback-panel",
    inspect_writing_signals: "demo-ner-button",
  };
  return byStep[stepId] || null;
}

const EMOTION_WORD_RE =
  /\b(happy|sad|angry|afraid|anxious|nervous|relieved|peaceful|joyful|grateful|calm|worried|lonely|frustrated|excited|scared|upset|proud)\b/i;
const SENSORY_WORD_RE =
  /\b(smell|scent|sound|noise|taste|flavor|touch|texture|warm|cold|bright|dark|rain|wind|sunlight|fragrance|whisper|echo)\b/i;

function normalizeSignalLabel(raw) {
  const label = String(raw || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!label) return "SIGNAL";
  if (label === "SENSORY_DETAIL" || label === "SENSORY_DETAILS") return "SENSORY_DETAIL";
  if (label === "SPECIFIC_NOUN" || label === "SPECIFIC_NOUNS") return "SPECIFIC_NOUN";
  if (label === "EMOTIONS") return "EMOTION";
  return label;
}

function isWordChar(ch) {
  return /[A-Za-z0-9_]/.test(ch || "");
}

function hasWordBoundaryAt(text, start, end) {
  const before = start > 0 ? text[start - 1] : "";
  const after = end < text.length ? text[end] : "";
  return !isWordChar(before) && !isWordChar(after);
}

function findPhraseMatches(text, phrase) {
  const source = String(text || "");
  const needle = String(phrase || "").trim();
  if (!source || !needle) return [];
  const lowerSource = source.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  const out = [];
  let searchFrom = 0;

  while (searchFrom < source.length) {
    const idx = lowerSource.indexOf(lowerNeedle, searchFrom);
    if (idx < 0) break;
    const end = idx + lowerNeedle.length;
    if (hasWordBoundaryAt(source, idx, end)) {
      out.push({ start: idx, end, text: source.slice(idx, end) });
    }
    searchFrom = idx + 1;
  }
  return out;
}

function addUniqueSpan(spans, spanMap, span) {
  const start = Number(span?.start);
  const end = Number(span?.end);
  const label = String(span?.label || "").trim();
  const text = String(span?.text || "").trim();
  if (!label || !text || Number.isNaN(start) || Number.isNaN(end) || end <= start)
    return;

  const key = `${label}|${start}|${end}|${text.toLowerCase()}`;
  if (spanMap.has(key)) return;
  spanMap.add(key);
  spans.push({ start, end, label, text });
}

function addRegexMatches(spans, spanMap, text, regex, label) {
  const r = new RegExp(regex.source, regex.flags);
  let m;
  while ((m = r.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    if (end <= start) continue;
    if (!hasWordBoundaryAt(text, start, end)) continue;
    addUniqueSpan(spans, spanMap, {
      start,
      end,
      label,
      text: text.slice(start, end),
    });
    if (m.index === r.lastIndex) r.lastIndex += 1;
  }
}

function resolveOverlaps(spans) {
  const ordered = [...spans].sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    const lenA = a.end - a.start;
    const lenB = b.end - b.start;
    return lenB - lenA;
  });

  const kept = [];
  for (const span of ordered) {
    if (kept.length === 0) {
      kept.push(span);
      continue;
    }

    const last = kept[kept.length - 1];
    if (span.start >= last.end) {
      kept.push(span);
      continue;
    }

    const spanLen = span.end - span.start;
    const lastLen = last.end - last.start;
    if (spanLen > lastLen) {
      kept[kept.length - 1] = span;
    }
  }
  return kept;
}

function normalizeSpanText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function selectDemoSpans(spans, options = {}) {
  const perLabelCap = Number(options.perLabelCap ?? 1);
  const totalCap = Number(options.totalCap ?? 2);
  const ordered = [...spans].sort((a, b) => a.start - b.start);

  // Dedupe repeated identical phrase+label across the passage; keep first occurrence only.
  const seenPhrasePerLabel = new Set();
  const perLabelCounts = new Map();
  const selected = [];

  for (const span of ordered) {
    const label = String(span.label || "").trim();
    const normalized = normalizeSpanText(span.text);
    if (!label || !normalized) continue;

    const phraseKey = `${label}|${normalized}`;
    if (seenPhrasePerLabel.has(phraseKey)) continue;

    const usedForLabel = perLabelCounts.get(label) || 0;
    if (usedForLabel >= perLabelCap) continue;
    if (selected.length >= totalCap) break;

    seenPhrasePerLabel.add(phraseKey);
    perLabelCounts.set(label, usedForLabel + 1);
    selected.push(span);
  }

  return selected;
}

// Retained for optional richer demo parsing experiments.
// eslint-disable-next-line no-unused-vars
function buildDemoAnnotations(text, highlights) {
  const sourceText = String(text || "");
  if (!sourceText.trim()) return [];
  const list = Array.isArray(highlights) ? highlights : [];
  const spans = [];
  const spanMap = new Set();

  for (const item of list) {
    const label = normalizeSignalLabel(item?.label || "SIGNAL");
    const phrase = String(item?.text || "").trim();
    if (!phrase) continue;

    const matches = findPhraseMatches(sourceText, phrase);
    for (const m of matches) {
      const candidate = {
        start: m.start,
        end: m.end,
        label,
        text: m.text,
      };

      if (label === "EMOTION") {
        const sample = `${m.text}`.toLowerCase();
        if (!EMOTION_WORD_RE.test(sample) && !/\b(feel|felt|feeling)\b/i.test(sample)) {
          continue;
        }
      }
      addUniqueSpan(spans, spanMap, candidate);
    }
  }

  // Strict demo emotion detection: only explicit emotion expressions.
  const emotionRegex =
    /\b(?:i\s+)?(?:feel|felt|feeling|was|am)\s+(?:very\s+|so\s+|really\s+)?(?:happy|sad|angry|afraid|anxious|nervous|relieved|peaceful|joyful|grateful|calm|worried|lonely|frustrated|excited|scared|upset|proud)\b/gi;
  let match;
  while ((match = emotionRegex.exec(sourceText)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (!hasWordBoundaryAt(sourceText, start, end)) continue;
    addUniqueSpan(spans, spanMap, {
      start,
      end,
      label: "EMOTION",
      text: sourceText.slice(start, end),
    });
  }

  // Sensory details: strict keyword-based spans.
  addRegexMatches(spans, spanMap, sourceText, SENSORY_WORD_RE, "SENSORY_DETAIL");

  // Specific nouns: proper nouns (capitalized words), excluding common pronouns/articles.
  const stopWords = new Set(["I", "The", "A", "An", "Hello", "Before", "After"]);
  addRegexMatches(
    spans,
    spanMap,
    sourceText,
    /\b[A-Z][a-z]{2,}\b/g,
    "SPECIFIC_NOUN"
  );
  // Remove obvious false positives from specific nouns.
  const filtered = spans.filter((s) => {
    if (s.label !== "SPECIFIC_NOUN") return true;
    return !stopWords.has(String(s.text || ""));
  });

  const nonOverlapping = resolveOverlaps(filtered);
  return selectDemoSpans(nonOverlapping, {
    perLabelCap: 1,
    totalCap: 2,
  });
}

function buildStep9LightweightAnnotations(text, highlights) {
  const sourceText = String(text || "");
  const list = Array.isArray(highlights) ? highlights : [];
  if (!sourceText.trim() || list.length === 0) return [];

  const out = [];
  const occupied = [];
  for (const item of list.slice(0, 6)) {
    const phrase = String(item?.text || "").trim();
    if (!phrase) continue;
    const lowerText = sourceText.toLowerCase();
    const lowerPhrase = phrase.toLowerCase();
    const start = lowerText.indexOf(lowerPhrase);
    if (start < 0) continue;
    const end = start + phrase.length;
    const overlaps = occupied.some((r) => !(end <= r.start || start >= r.end));
    if (overlaps) continue;
    occupied.push({ start, end });
    out.push({
      start,
      end,
      label: normalizeSignalLabel(item?.label || "SIGNAL"),
      text: sourceText.slice(start, end),
    });
  }
  return out;
}

export default function DemoLessonFlow({ studentId, source = "dashboard", returnTo = "", onComplete }) {
  const navigate = useNavigate();
  const [demoData, setDemoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [demoText, setDemoText] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [pasteNotice, setPasteNotice] = useState("");
  const textareaRef = useRef(null);
  const prefillAppliedRef = useRef(false);
  const typingTimerRef = useRef(null);
  const [isDemoAutoTyping, setIsDemoAutoTyping] = useState(false);
  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [coachFeedbackVisible, setCoachFeedbackVisible] = useState(false);
  const [nerVisible, setNerVisible] = useState(false);
  const [showWritingSignals, setShowWritingSignals] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [readTaskConfirmed, setReadTaskConfirmed] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [spotlightRefreshTick, setSpotlightRefreshTick] = useState(0);
  const demoPerformanceBand = useMemo(
    () =>
      getPerformanceBand(72, {
        id: "W1L2",
        objective: "Develop confidence in generating raw, unfiltered ideas",
      }),
    []
  );
  const completingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    async function loadData() {
      try {
        setLoading(true);
        const data = await loadDemoLesson();
        if (cancelled) return;
        setDemoData(data);
        setDemoText("");
        setWordCount(0);
        setPasteNotice("");
        if (typingTimerRef.current) {
          window.clearTimeout(typingTimerRef.current);
          typingTimerRef.current = null;
        }
        setIsDemoAutoTyping(false);
        prefillAppliedRef.current = false;
        setLoadError("");
      } catch (err) {
        if (!cancelled) setLoadError("Could not load demo content.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadData();
    return () => {
      cancelled = true;
    };
  }, []);

  const currentStepId = STEP_IDS[currentStepIndex] || "complete";
  const textEntryUnlocked =
    currentStepIndex >= START_WRITING_STEP_INDEX && !isDemoAutoTyping;
  const minWordCount = Number(demoData?.min_word_count || 60);
  const currentStepNumber = Math.min(currentStepIndex + 1, STEP_IDS.length);
  const canShowWritingSignals =
    feedbackVisible || currentStepId === "inspect_writing_signals";
  const isShowingWritingSignals = canShowWritingSignals && showWritingSignals;
  const annotationInputText = useMemo(
    () => String(demoText || "").slice(0, DEMO_ANNOTATION_MAX_CHARS),
    [demoText]
  );
  const mockAnnotations = useMemo(() => {
    if (!isShowingWritingSignals) return [];
    return buildStep9LightweightAnnotations(annotationInputText, demoData?.mock_ner?.highlights);
  }, [isShowingWritingSignals, annotationInputText, demoData]);
  const wordCountState =
    wordCount < minWordCount * 0.7
      ? "red"
      : wordCount < minWordCount
      ? "orange"
      : "green";
  const wordCountLabel =
    wordCountState === "green"
      ? "Good length"
      : `Minimum ${minWordCount} words required`;
  const emphasizeMinimumBadge =
    currentStepId === "reach_minimum" && wordCount < minWordCount;
  const wordCountBadgeStyle = {
    display: "inline-block",
    padding: emphasizeMinimumBadge ? "5px 12px" : "4px 10px",
    borderRadius: 999,
    fontWeight: emphasizeMinimumBadge ? 800 : 700,
    lineHeight: 1.2,
    backgroundColor:
      emphasizeMinimumBadge
        ? "#facc15"
        : wordCountState === "red"
        ? "#fee2e2"
        : wordCountState === "orange"
        ? "#fef3c7"
        : "#dcfce7",
    border:
      emphasizeMinimumBadge
        ? "2px solid #f59e0b"
        : wordCountState === "red"
        ? "1px solid #fecaca"
        : wordCountState === "orange"
        ? "1px solid #fde68a"
        : "1px solid #bbf7d0",
    color: emphasizeMinimumBadge ? "#111111" : "#111827",
    boxShadow: emphasizeMinimumBadge ? "0 0 0 2px rgba(250, 204, 21, 0.35)" : "none",
  };

  const stepInstructionMap = useMemo(() => {
    const map = {};
    for (const step of demoData?.steps || []) {
      map[step.id] = step.instruction;
    }
    return map;
  }, [demoData]);

  const stateForValidation = {
    readTaskConfirmed,
    feedbackVisible,
    coachFeedbackVisible,
    nerVisible,
    showWritingSignals,
    allowInspectStepNext: currentStepId === "inspect_writing_signals",
  };

  const normalizedStudentId = useMemo(
    () => String(studentId || "SM01K4R9").toUpperCase(),
    [studentId]
  );

  const fallbackReturnTo = useMemo(() => {
    if (source === "auto_w1l1") {
      return `/student/${normalizedStudentId}/week/1/lesson/1`;
    }
    return `/student/dashboard/${normalizedStudentId}`;
  }, [normalizedStudentId, source]);

  const resolvedReturnTo = useMemo(
    () => String(returnTo || "").trim() || fallbackReturnTo,
    [returnTo, fallbackReturnTo]
  );

  function appendReturnMarker(path) {
    const raw = String(path || "").trim();
    if (!raw) return raw;
    if (source !== "auto_w1l1") return raw;

    const [base, hash = ""] = raw.split("#", 2);
    const joiner = base.includes("?") ? "&" : "?";
    const out = base.includes("from_demo_completion=")
      ? base
      : `${base}${joiner}from_demo_completion=1`;
    return hash ? `${out}#${hash}` : out;
  }

  function goToNextStep() {
    if (!canAdvanceStep(currentStepId, stateForValidation)) return;
    setCurrentStepIndex((i) => Math.min(i + 1, STEP_IDS.length - 1));
  }

  async function completeDemo() {
    if (completingRef.current) return;
    completingRef.current = true;
    try {
      if (source === "auto_w1l1") {
        await postJSON("/api/demo/complete-once", { student_id: normalizedStudentId });
      }
    } catch {
      // non-blocking: still return the learner to their requested destination.
    }

    try {
      if (typeof onComplete === "function") {
        await onComplete({
          source,
          returnTo: resolvedReturnTo,
          studentId: normalizedStudentId,
        });
      }
    } catch {
      // Ignore callback errors and continue canonical navigation.
    }

    setIsCompleted(true);
    navigate(appendReturnMarker(resolvedReturnTo), { replace: true });
  }

  function skipDemo() {
    setShowSkipConfirm(true);
  }

  function applyDemoTextUpdate(nextText, source = "typing") {
    const normalized = String(nextText || "");
    const clipped = normalized.slice(0, MAX_RESPONSE_CHARS);
    if (source === "paste") {
      setPasteNotice(
        clipped.length < normalized.length
          ? `Pasted text was trimmed to ${MAX_RESPONSE_CHARS} characters to keep the page responsive.`
          : ""
      );
    } else if (pasteNotice) {
      setPasteNotice("");
    }
    setDemoText(clipped);
    setWordCount(countWords(clipped));
  }

  function handleTextChange(e) {
    if (!textEntryUnlocked) return;
    applyDemoTextUpdate(e.target.value, "typing");
  }

  function handleTextPaste(e) {
    if (!textEntryUnlocked) {
      e.preventDefault();
      return;
    }
    const pasted = e?.clipboardData?.getData("text");
    if (typeof pasted !== "string") return;
    e.preventDefault();

    const target = e.target;
    const start = Number.isInteger(target.selectionStart)
      ? target.selectionStart
      : demoText.length;
    const end = Number.isInteger(target.selectionEnd)
      ? target.selectionEnd
      : demoText.length;

    const selectedLength = Math.max(0, end - start);
    const availableSpace = Math.max(
      0,
      MAX_RESPONSE_CHARS - (demoText.length - selectedLength)
    );
    const clippedPaste = pasted.slice(0, availableSpace);
    const nextText =
      demoText.slice(0, start) + clippedPaste + demoText.slice(end);
    applyDemoTextUpdate(nextText, "paste");

    const nextCursor = start + clippedPaste.length;
    window.requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.selectionStart = nextCursor;
        textareaRef.current.selectionEnd = nextCursor;
      }
    });
  }

  function handleDemoFeedbackRequest() {
    setFeedbackVisible(true);
    setCoachFeedbackVisible(true);
    setNerVisible(true);
    if (currentStepId === "click_feedback") goToNextStep();
  }

  function handleDemoTextareaActivate() {
    if (currentStepId !== "start_writing") return;
    setCurrentStepIndex((i) => Math.min(i + 1, STEP_IDS.length - 1));
  }

  async function handleConfirmSkipDemo() {
    setShowSkipConfirm(false);
    await completeDemo();
  }

  function handleCancelSkipDemo() {
    setShowSkipConfirm(false);
  }

  function scrollToDemoTopSection() {
    const topTarget =
      document.querySelector('[data-demo-target="demo-task"]') ||
      document.querySelector('[data-demo-target="demo-writing-focus"]');
    if (!topTarget) return;
    topTarget.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  }

  useEffect(() => {
    if (currentStepId === "start_writing" && demoText.trim().length >= 15) {
      setCurrentStepIndex((i) => Math.min(i + 1, STEP_IDS.length - 1));
    }
  }, [currentStepId, demoText]);

  useEffect(() => {
    if (currentStepId === "reach_minimum" && wordCount >= minWordCount) {
      setCurrentStepIndex((i) => Math.min(i + 1, STEP_IDS.length - 1));
    }
  }, [currentStepId, wordCount, minWordCount]);

  useEffect(() => {
    if (currentStepId !== "review_feedback") return;
    if (!feedbackVisible) return;

    const rafId = window.requestAnimationFrame(() => {
      const target = getStepTarget(currentStepId);
      if (!target) return;
      const node = document.querySelector(`[data-demo-target="${target}"]`);
      if (!node) return;
      node.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });

      // The spotlight overlay no longer listens to scroll continuously,
      // so force a refresh after smooth scrolling settles.
      window.setTimeout(() => setSpotlightRefreshTick((n) => n + 1), 120);
      window.setTimeout(() => setSpotlightRefreshTick((n) => n + 1), 420);
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [currentStepId, feedbackVisible]);

  useEffect(() => {
    // Refresh spotlight when target step changes.
    setSpotlightRefreshTick((n) => n + 1);
  }, [currentStepId]);

  useEffect(() => {
    if (currentStepId !== "reach_minimum") return;
    if (prefillAppliedRef.current) return;
    const prefill = String(demoData?.prefill_text || "").trim();
    if (!prefill) return;
    prefillAppliedRef.current = true;
    setIsDemoAutoTyping(true);
    setDemoText("");
    setWordCount(0);
    setPasteNotice("");

    let index = 0;
    const CHARS_PER_TICK = 3;
    const TICK_MS = 22;

    const typeNext = () => {
      index = Math.min(prefill.length, index + CHARS_PER_TICK);
      const next = prefill.slice(0, index);
      setDemoText(next);
      setWordCount(countWords(next));

      if (index >= prefill.length) {
        typingTimerRef.current = null;
        setIsDemoAutoTyping(false);
        return;
      }
      typingTimerRef.current = window.setTimeout(typeNext, TICK_MS);
    };

    typingTimerRef.current = window.setTimeout(typeNext, 120);
  }, [currentStepId, demoData]);

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        window.clearTimeout(typingTimerRef.current);
        typingTimerRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="demo-flow">
        <div className="lp-loading">Loading guided demo...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="demo-flow">
        <div className="lp-box lp-warn">
          <p>{loadError}</p>
          <button type="button" className="lp-btn lp-primary" onClick={completeDemo}>
            Continue to Week 1 Objectives
          </button>
        </div>
      </div>
    );
  }

  if (!demoData || isCompleted) return null;

  const isCenteredPanel = currentStepId === "welcome" || currentStepId === "complete";
  const targetId = getStepTarget(currentStepId);

  const actionConfigByStep = {
    welcome: {
      label: "Begin",
      onClick: goToNextStep,
      disabled: false,
    },
    read_task: {
      label: "I have read the task",
      onClick: () => {
        setReadTaskConfirmed(true);
        setCurrentStepIndex((i) => Math.min(i + 1, STEP_IDS.length - 1));
      },
      disabled: false,
    },
    start_writing: {
      label: "Next",
      onClick: handleDemoTextareaActivate,
      disabled: false,
    },
    notice_word_count: {
      label: "Next",
      onClick: goToNextStep,
      disabled: false,
    },
    review_feedback: {
      label: "I have reviewed the feedback",
      onClick: goToNextStep,
      disabled: !feedbackVisible,
    },
    coachFeedback: {
      label: "I have read the coach feedback",
      onClick: goToNextStep,
      disabled: !coachFeedbackVisible,
    },
    inspect_writing_signals: {
      label: "",
      onClick: goToNextStep,
      disabled: !showWritingSignals,
    },
    complete: {
      label: demoData.completion_cta || "Continue",
      onClick: completeDemo,
      disabled: false,
    },
  };

  const actionConfig = actionConfigByStep[currentStepId] || null;

  const instruction =
    stepInstructionMap[currentStepId] ||
    "Follow the instruction to continue the guided demo.";
  const instructorPlacement = currentStepNumber >= 9 ? "bottom-right" : "top-right";
  const spotlightEnabled = !isCenteredPanel;

  return (
    <div className="demo-flow">
      <DemoSpotlightOverlay
        enabled={spotlightEnabled}
        targetId={targetId}
        refreshTick={spotlightRefreshTick}
        followScroll={true}
      />

      <div className="demo-card">
        <h2 className="lp-h2">
          <span>{demoData.title}</span>
        </h2>
        <p className="demo-card__intro">{demoData.intro}</p>

        <div className="lp-task demo-task-block" data-demo-target="demo-task">
          <span className="demo-task-label">Task:</span>
          <span className="demo-task-copy">{demoData.lesson_task}</span>
        </div>

        <div data-demo-target="demo-writing-focus">
          <div className="lp-task-area demo-writing-window-wrap" data-demo-target="demo-textarea">
            {isShowingWritingSignals ? (
              <div
                className="lp-textarea lp-textarea-signals demo-writing-window"
                data-demo-target="demo-ner-panel"
                aria-live="polite"
              >
                <HighlightedCreativeText
                  text={demoText}
                  annotations={mockAnnotations}
                  showInlineHighlights={true}
                />
              </div>
            ) : (
              <textarea
                ref={textareaRef}
                className={`lp-textarea demo-writing-window ${
                  textEntryUnlocked ? "" : "is-locked"
                }`.trim()}
                value={demoText}
                onChange={handleTextChange}
                onPaste={handleTextPaste}
                onClick={handleDemoTextareaActivate}
                onFocus={handleDemoTextareaActivate}
                readOnly={!textEntryUnlocked}
                placeholder="Write your response..."
              />
            )}
          </div>
          {pasteNotice ? (
            <div
              className="lp-box lp-warn"
              style={{ marginTop: 10, fontSize: 13, padding: "8px 10px" }}
            >
              {pasteNotice}
            </div>
          ) : null}

          <div
            className="lp-status demo-status-row"
          >
            <span className="demo-status-left">Status: Demo mode</span>
            <div className="demo-status-right">
              <button
                type="button"
                className={`lp-btn lp-writing-signals-btn ${
                  isShowingWritingSignals ? "is-active" : "is-inactive"
                } ${canShowWritingSignals ? "" : "lp-disabled"}`}
                data-demo-target="demo-ner-button"
                onClick={() => {
                  if (!canShowWritingSignals) return;
                  setShowWritingSignals((prev) => {
                    const next = !prev;
                    if (currentStepId === "inspect_writing_signals" && next) {
                      setCurrentStepIndex((i) => Math.min(i + 1, STEP_IDS.length - 1));
                    }
                    if (next) {
                      window.setTimeout(scrollToDemoTopSection, 40);
                    }
                    return next;
                  });
                }}
                disabled={!canShowWritingSignals}
                title={
                  canShowWritingSignals
                    ? "Toggle inline writing signals."
                    : "Get feedback first."
                }
              >
                Writing Signals
              </button>
              <div
                data-demo-target="demo-wordcount"
                className={`demo-wordcount-cluster ${
                  currentStepId === "reach_minimum" ? "is-priority" : ""
                }`.trim()}
              >
                <span>{`${wordCount} words`}</span>
                <span style={wordCountBadgeStyle}>{wordCountLabel}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="lp-actions demo-actions-row">
          <button
            type="button"
            className={`lp-btn ${currentStepId === "click_feedback" ? "lp-primary" : "lp-ghost"} ${
              currentStepId === "click_feedback" ? "" : "lp-disabled"
            }`}
            data-demo-target="demo-feedback-button"
            onClick={handleDemoFeedbackRequest}
            disabled={currentStepId !== "click_feedback"}
          >
            Get Feedback
          </button>
        </div>

        {feedbackVisible ? (
          <div className="demo-feedback-wrap" data-demo-target="demo-feedback-panel">
            <section className="lp-box fb-card fb-tone--soft">
              <header className="fb-head">
                <div className="fb-status-copy">
                  <div className="fb-status-heading">{demoPerformanceBand.heading}</div>
                  <div className="fb-status-subline">{demoPerformanceBand.message}</div>
                </div>

                <div className="fb-pills" aria-label="Feedback summary">
                  <div className="fb-pill">
                    Creative development: <b>72</b> / 100
                  </div>
                  <div className="fb-pill">
                    Pass mark: <b>60</b>
                  </div>
                  <div className="fb-pill">
                    Attempt <b>1</b> of <b>2</b>
                  </div>
                </div>
              </header>

              <div className="fb-grid" role="list">
                <div className="fb-row" role="listitem">
                  <div className="fb-label">Creativity</div>
                  <div className="fb-text">
                    {demoData.mock_feedback.creativity}
                  </div>
                </div>
                <div className="fb-row" role="listitem">
                  <div className="fb-label">Clarity</div>
                  <div className="fb-text">
                    {demoData.mock_feedback.clarity}
                  </div>
                </div>
                <div className="fb-row" role="listitem">
                  <div className="fb-label">Task completion</div>
                  <div className="fb-text">
                    {demoData.mock_feedback.task_completion}
                  </div>
                </div>
              </div>
            </section>
          </div>
        ) : null}

        {coachFeedbackVisible ? (
          <div className="lp-box demo-coach-feedback-card" data-demo-target="demo-coach-feedback-panel">
            <div className="lp-coach-feedback-head">
              <h3 className="lp-coach-feedback-title">Coach feedback</h3>
              <p className="lp-coach-feedback-copy">Use this to improve your next draft.</p>
            </div>
            <div className="demo-coach-feedback-body">
              <p>{String(demoData?.mock_feedback?.coach_feedback || "")}</p>
            </div>
          </div>
        ) : null}
      </div>

      <DemoInstructorPanel
        title="Guided Instructor"
        intro={
          currentStepId === "complete"
            ? "You have completed the guided demo, and may replay this from the Dashboard."
            : ""
        }
        stepLabel={`Step ${currentStepNumber} of ${STEP_IDS.length}`}
        instruction={instruction}
        actionLabel={actionConfig?.label || ""}
        onAction={actionConfig?.onClick}
        actionDisabled={Boolean(actionConfig?.disabled)}
        onSkip={skipDemo}
        centered={isCenteredPanel}
        placement={instructorPlacement}
      />

      {showSkipConfirm ? (
        <div
          className="demo-confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Skip demo confirmation"
          onClick={handleCancelSkipDemo}
        >
          <div
            className="demo-confirm-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="demo-confirm-title">Skip Guided Demo?</div>
            <div className="demo-confirm-text">
              Are you sure you want to skip this demo?
            </div>
            <div className="demo-confirm-note">
              Don't worry, you can always replay this from the Dashboard.
            </div>
            <div className="demo-confirm-actions">
              <button
                type="button"
                className="lp-btn lp-ghost"
                onClick={handleCancelSkipDemo}
              >
                Cancel
              </button>
              <button
                type="button"
                className="lp-btn lp-primary"
                onClick={handleConfirmSkipDemo}
              >
                Skip Demo
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
